import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../../styles/app-modal.css";
import "../../../styles/form-select-modal.css";

const LAST_FORM_KEY = "requests:lastFormId";
const SEARCH_THRESHOLD = 6;

const FORM_ICONS = {
  epr: "fa-shopping-cart",
  offsetting: "fa-exchange",
  prospecting: "fa-binoculars",
  transmittal: "fa-paper-plane",
  "explain-why": "fa-commenting",
  ccar: "fa-exclamation-triangle",
  "so-lo": "fa-truck",
  edsr: "fa-bar-chart",
};

const readLastFormId = () => {
  try {
    return localStorage.getItem(LAST_FORM_KEY) || "";
  } catch {
    return "";
  }
};

const saveLastFormId = (formId) => {
  try {
    localStorage.setItem(LAST_FORM_KEY, formId);
  } catch {
    // Storage can be blocked; remembering the last form is only a convenience.
  }
};

function FormSelectModal({
  modalTitle,
  handleShowingModal,
  formOptions = [],
  isLoading = false,
}) {
  const forms = useMemo(
    () => formOptions.filter((form) => form.add_page && form.form_details),
    [formOptions],
  );

  const [selectedForm, setSelectedForm] = useState("");
  const [query, setQuery] = useState("");

  const dialogRef = useRef(null);
  const searchRef = useRef(null);

  // Preselect the form used last time, or the only form there is.
  useEffect(() => {
    if (selectedForm || forms.length === 0) return;

    const lastFormId = readLastFormId();
    const remembered = forms.find((form) => form.form_id === lastFormId);

    if (remembered) {
      setSelectedForm(remembered.form_id);
    } else if (forms.length === 1) {
      setSelectedForm(forms[0].form_id);
    }
  }, [forms, selectedForm]);

  const visibleForms = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (!keyword) return forms;

    return forms.filter((form) =>
      form.form_details.name.toLowerCase().includes(keyword),
    );
  }, [forms, query]);

  // A selection hidden by the search doesn't count, so Continue never opens a
  // form the user can't see.
  const activeForm = visibleForms.some((form) => form.form_id === selectedForm)
    ? selectedForm
    : "";

  const showSearch = forms.length > SEARCH_THRESHOLD;

  // Focus the search box, or the selected/first option, once forms are in.
  useEffect(() => {
    if (isLoading) return;

    if (showSearch) {
      searchRef.current?.focus();
      return;
    }

    const target =
      dialogRef.current?.querySelector(".fsm-option input:checked") ||
      dialogRef.current?.querySelector(".fsm-option input");

    (target || dialogRef.current)?.focus();
    // Only on open/when loading finishes, not on every selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, showSearch]);

  // Give focus back to whatever opened the picker (e.g. "+ Add New Request").
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    return () => previouslyFocused?.focus?.();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        handleShowingModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleShowingModal]);

  const proceed = (formId = activeForm) => {
    if (!formId) return;

    saveLastFormId(formId);
    window.location.href = `/requests?p=new-request&f=${formId}`;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    proceed();
  };

  const handleSearchKeyDown = (event) => {
    // Enter in the search box picks the top match when nothing is selected.
    if (event.key === "Enter" && !activeForm && visibleForms.length > 0) {
      event.preventDefault();
      proceed(visibleForms[0].form_id);
    }
  };

  const renderBody = () => {
    if (isLoading) {
      return (
        <ul className="fsm-list" aria-hidden="true">
          {[0, 1, 2].map((key) => (
            <li key={key} className="fsm-skeleton" />
          ))}
        </ul>
      );
    }

    if (forms.length === 0) {
      return (
        <div className="fsm-empty">
          <i className="fa fa-folder-open-o" aria-hidden="true" />
          <p className="fsm-empty-title">No forms available</p>
          <p className="fsm-empty-text">
            You don't have access to any request forms yet. Ask your
            administrator to give you access.
          </p>
        </div>
      );
    }

    if (visibleForms.length === 0) {
      return (
        <div className="fsm-empty">
          <i className="fa fa-search" aria-hidden="true" />
          <p className="fsm-empty-title">No forms match "{query.trim()}"</p>
          <button
            type="button"
            className="fsm-link"
            onClick={() => {
              setQuery("");
              searchRef.current?.focus();
            }}
          >
            Clear search
          </button>
        </div>
      );
    }

    return (
      <div
        className="fsm-list"
        role="radiogroup"
        aria-labelledby="fsm-title"
      >
        {visibleForms.map((form) => {
          const { name, type } = form.form_details;
          const isSelected = form.form_id === activeForm;

          return (
            <label
              key={form.form_id}
              className={`fsm-option${isSelected ? " is-selected" : ""}`}
              onDoubleClick={() => proceed(form.form_id)}
            >
              <input
                type="radio"
                name="fsm-form"
                className="fsm-radio"
                value={form.form_id}
                checked={isSelected}
                onChange={() => setSelectedForm(form.form_id)}
              />

              <span className="fsm-icon" aria-hidden="true">
                <i className={`fa ${FORM_ICONS[type] || "fa-file-text-o"}`} />
              </span>

              <span className="fsm-name">{name}</span>

              <span className="fsm-check" aria-hidden="true">
                <i className="fa fa-check" />
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="modal-container app-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) handleShowingModal();
      }}
    >
      <form
        ref={dialogRef}
        className="modal-dialog app-modal-dialog fsm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fsm-title"
        aria-describedby="fsm-subtitle"
        tabIndex={-1}
        onSubmit={handleSubmit}
      >
        <header className="fsm-head">
          <div>
            <h3 className="fsm-title" id="fsm-title">
              {modalTitle || "New request"}
            </h3>
            <p className="fsm-subtitle" id="fsm-subtitle">
              Choose the form you want to fill out.
            </p>
          </div>

          <button
            type="button"
            className="fsm-close"
            onClick={handleShowingModal}
            aria-label="Close"
          >
            <i className="fa fa-times" aria-hidden="true" />
          </button>
        </header>

        {showSearch && !isLoading && (
          <div className="fsm-search">
            <i className="fa fa-search" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder="Search forms"
              aria-label="Search forms"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>
        )}

        <div className="fsm-body">{renderBody()}</div>

        <footer className="fsm-foot">
          <span className="fsm-hint">Double-click a form to open it</span>

          <div className="fsm-actions">
            <button
              type="button"
              className="fsm-btn fsm-btn-ghost"
              onClick={handleShowingModal}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="fsm-btn fsm-btn-primary"
              disabled={!activeForm}
            >
              Continue
              <i className="fa fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}

export default FormSelectModal;
